import { useCallback, useState, type Dispatch, SetStateAction } from 'react';
import { useStableCallback } from './use-stable-callback';

type SetStateFn<T> = Dispatch<SetStateAction<T>>;

interface UseControllableStateOptions<T> {
   prop?: T;
   defaultProp: T;
   onChange?: (value: T) => void;
}

/**
 * Hook for managing controlled and uncontrolled state in components.
 *
 * @param options Configuration for the controllable state
 * @returns Tuple of [currentValue, setValue] similar to useState
 *
 * @example
 * ```tsx
 * // Uncontrolled usage
 * function MyComponent({ defaultOpen = false }) {
 *   const [open, setOpen] = useControllableState({
 *     defaultProp: defaultOpen
 *   });
 * }
 *
 * // Controlled usage
 * function MyComponent({ open, onOpenChange }) {
 *   const [openState, setOpenState] = useControllableState({
 *     prop: open,
 *     onChange: onOpenChange
 *   });
 * }
 *
 * // Support both patterns
 * function MyComponent({ open, defaultOpen = false, onOpenChange }) {
 *   const [openState, setOpenState] = useControllableState({
 *     prop: open,
 *     defaultProp: defaultOpen,
 *     onChange: onOpenChange
 *   });
 * }
 * ```
 */
export function useControllableState<T>({
   prop,
   defaultProp,
   onChange,
}: UseControllableStateOptions<T>): [T, SetStateFn<T>] {
   const [uncontrolledState, setUncontrolledState] = useState<T>(defaultProp);
   const isControlled = prop !== undefined;
   const state = isControlled ? prop : uncontrolledState;

   const stableOnChange = useStableCallback(onChange);

   const setState = useCallback<SetStateFn<T>>(
      value => {
         if (isControlled) {
            const nextValue = isFunction(value) ? value(state) : value;
            if (nextValue != prop) stableOnChange?.(nextValue);
         } else {
            setUncontrolledState(value);
         }
      },
      [isControlled, prop, state, stableOnChange]
   );

   return [state, setState];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isFunction(value: unknown): value is (...args: any[]) => any {
   return typeof value === 'function';
}
